import { useId, useMemo } from "react";
import {
  bracketForPreset,
  entryAccountOf,
  gateAction,
  isReducing,
  joinPrice,
  orderWords,
  unknownSides,
  useTrading,
  type BracketPreset,
  type ContractFacts,
  type EntryOrigin,
  type EntryTif,
  type EntryType,
  type Gate,
  type GateAction,
  type OrderBracket,
  type OrderIntent,
  type TradeSide,
} from "@helm/core";
import { formatTicks, type TradingActionId } from "@helm/ui";
import { priceFormatOf, useMarket } from "../md/marketData";
import { useOpenPosition, useTradingViewSlice } from "../viewmodel/account";
import { useBusy, useGateContext } from "./control";
import { exitAction, placeOrder, requestFlattenAll, reverseAction } from "./actions";
import { anchorBelow, type Anchor } from "./hosts";
import { pushToast } from "../components/Toasts";

/**
 * Everything an order-entry surface (order pad, DOM ladder) needs for one symbol: the account it
 * trades (the top-bar selection — a specific account; "All accounts" disables order entry), the
 * resolved contract and tick format, the position, the gates, and the actions. Each widget
 * instance gets its own busy keys, so its buttons disable only while *its* command is in flight.
 *
 * With a bracket preset selected, every new order that opens or adds to a position goes out WITH
 * its bracket (sized for the order quantity, $ legs re-derived — `brackets/order.ts`); the gateway
 * places the stop and target as the entry fills (ADR-0025). An order that only reduces the
 * position carries no bracket. A bracket that cannot be sized refuses the order, never sends it
 * bare.
 */
export function useOrderEntry(
  symbol: string | null,
  origin: EntryOrigin,
  opts: {
    /** The selected bracket preset (null: "No bracket"). */
    bracket?: BracketPreset | null;
    /** Contract facts for sizing $ legs (live instrument, else the CME table). */
    facts?: ContractFacts | null;
  } = {},
) {
  const preset = opts.bracket ?? null;
  const facts = opts.facts ?? null;
  const scope = useId();
  const m = useMarket(symbol);
  const info = m?.info ?? null;
  // One object per instrument, so memoized cells keyed on it survive quotes.
  const fmt = useMemo(() => priceFormatOf(info), [info]);
  // The selection the surface's account picker shows (`entryAccountOf`): `account` is "all", an
  // account id, or null without accounts; `accountId` is where orders go (null: none may be sent).
  const selection = useTradingViewSlice((v) => v.selection, Object.is);
  const { account, accountId } = entryAccountOf(selection);
  const contract = m?.info?.symbol ?? null;
  const pos = useOpenPosition(accountId, contract);
  const netQty = pos?.netQty ?? 0;
  const orders = useTrading((s) => s.orders);
  const unknown = useMemo(
    () => unknownSides(orders.values(), accountId, contract),
    [orders, accountId, contract],
  );
  const ctx = useGateContext();
  const bid = m?.bidTicks ?? null;
  const ask = m?.askTicks ?? null;

  const placeGateAction = (
    side: TradeSide,
    qty: number,
    needsPrice = false,
    price: number | null = null,
  ): GateAction => ({
    kind: "place",
    accountId: account,
    side,
    qty,
    contract,
    tickKnown: fmt !== null,
    needsPrice,
    price,
    netQty,
    unknownSides: unknown,
  });
  /** The preset's bracket for this order, none, or why it cannot be sized. */
  const bracketFor = (
    side: TradeSide,
    qty: number,
  ): { ok: true; bracket: OrderBracket | null } | { ok: false; message: string } => {
    if (preset === null || isReducing(side, qty, netQty)) return { ok: true, bracket: null };
    const r = bracketForPreset(preset, qty, facts);
    return r.ok ? { ok: true, bracket: r.bracket } : r;
  };
  const placeGate = (side: TradeSide, qty: number, needsPrice = false, price: number | null = null): Gate => {
    const g = gateAction(ctx, placeGateAction(side, qty, needsPrice, price));
    if (!g.ok) return g;
    const b = bracketFor(side, qty);
    return b.ok
      ? g
      : { ok: false, code: "NOT_AVAILABLE", reason: `Bracket ${preset?.name ?? ""}: ${b.message}` };
  };
  const exitGate = (kind: "exitSymbol" | "cancelAll" | "flattenAccount" | "reverse"): Gate => {
    if (kind === "exitSymbol") return gateAction(ctx, { kind, accountId: account, contract });
    if (kind === "reverse") {
      return gateAction(ctx, { kind, accountId: account, contract, netQty, unknownSides: unknown });
    }
    return gateAction(ctx, { kind, accountId: account });
  };
  const flattenAllGate = gateAction(ctx, { kind: "flattenAll" });

  const priceText = (t: number): string => (fmt ? formatTicks(t, fmt) : `${String(t)} t`);

  const place = (
    key: string,
    o: {
      side: TradeSide;
      type: EntryType;
      qty: number;
      limitTicks?: number;
      stopTicks?: number;
      origin?: EntryOrigin;
      tif?: EntryTif;
      /** An explicit bracket (the ticket's one-off SL/TP); otherwise the selected preset's. */
      bracket?: OrderBracket | null;
      /** A TP / SL for the whole open position (chart position-line drag): never a bracket. */
      protectPosition?: boolean;
    },
    anchor: Anchor | null,
  ): void => {
    const fromPreset =
      o.protectPosition === true ? { ok: true as const, bracket: null } : bracketFor(o.side, o.qty);
    if (!fromPreset.ok) {
      pushToast(
        {
          tone: "warn",
          title: "Order not sent",
          body: `Bracket ${preset?.name ?? ""}: ${fromPreset.message}`,
        },
        { ttlMs: 6000 },
      );
      return;
    }
    const bracket =
      o.protectPosition === true ? null : o.bracket !== undefined ? o.bracket : fromPreset.bracket;
    if (accountId === null || contract === null) {
      const g = placeGate(o.side, o.qty);
      if (!g.ok) pushToast({ tone: "warn", title: "Order not sent", body: g.reason }, { ttlMs: 5000 });
      return;
    }
    const intent: OrderIntent = {
      accountId,
      contract,
      side: o.side,
      type: o.type,
      qty: o.qty,
      origin: o.origin ?? origin,
      ...(o.tif !== undefined ? { tif: o.tif } : {}),
      ...(o.limitTicks !== undefined ? { limitTicks: o.limitTicks } : {}),
      ...(o.stopTicks !== undefined ? { stopTicks: o.stopTicks } : {}),
      ...(bracket ? { bracket } : {}),
      ...(o.protectPosition === true ? { protectPosition: true } : {}),
    };
    const price = o.limitTicks ?? o.stopTicks ?? null;
    void placeOrder(
      `${scope}:${key}`,
      intent,
      placeGateAction(o.side, o.qty, o.type !== "market", price),
      orderWords(intent, priceText),
      anchor,
    );
  };

  const market = (side: TradeSide, qty: number, anchor: Anchor | null, from: EntryOrigin = origin): void => {
    place(side === "buy" ? "buy-mkt" : "sell-mkt", { side, type: "market", qty, origin: from }, anchor);
  };
  const join = (side: TradeSide, qty: number, anchor: Anchor | null, from: EntryOrigin = origin): void => {
    const px = joinPrice(side, bid, ask);
    if (px === null) {
      pushToast(
        { tone: "warn", title: side === "buy" ? "Join Bid" : "Join Ask", body: "No best bid/ask yet" },
        { ttlMs: 4000 },
      );
      return;
    }
    place(
      side === "buy" ? "join-bid" : "join-ask",
      { side, type: "limit", qty, limitTicks: px, origin: from },
      anchor,
    );
  };
  const exitSymbol = (): void => {
    void exitAction(
      `${scope}:exit`,
      "EXIT_AND_CANCEL_SYMBOL",
      accountId,
      contract,
      `Exit at Mkt & Cxl ${contract ?? ""}`.trim(),
    );
  };
  const cancelAll = (): void => {
    void exitAction(
      `${scope}:cxl-all`,
      "CANCEL_ALL_ACCOUNT",
      accountId,
      null,
      `Cancel all orders · ${accountId ?? ""}`,
    );
  };
  const reverse = (anchor: Anchor | null, valid?: () => boolean): void => {
    void reverseAction(`${scope}:reverse`, accountId, contract, netQty, unknown, anchor, valid);
  };
  const flatten = (): void => {
    if (account === "all") {
      requestFlattenAll();
      return;
    }
    void exitAction(
      `${scope}:flatten`,
      "FLATTEN_ACCOUNT",
      accountId,
      null,
      `Exit All Positions & Cxl All Orders · ${accountId ?? ""}`,
    );
  };

  /** Hotkeys B S J A R F C X for this surface (SPEC §9.10). */
  const hotkey = (action: TradingActionId, qty: number, el: Element): void => {
    const anchor = anchorBelow(el);
    switch (action) {
      case "trade.buy_market":
        market("buy", qty, anchor, "HOTKEY");
        return;
      case "trade.sell_market":
        market("sell", qty, anchor, "HOTKEY");
        return;
      case "trade.join_bid":
        join("buy", qty, anchor, "HOTKEY");
        return;
      case "trade.join_ask":
        join("sell", qty, anchor, "HOTKEY");
        return;
      case "trade.exit_cancel":
        exitSymbol();
        return;
      case "trade.cancel_all":
        cancelAll();
        return;
      case "trade.flatten":
        flatten();
        return;
      case "trade.flatten_all":
        requestFlattenAll();
        return;
      case "trade.reverse":
        reverse(anchor);
        return;
    }
  };

  return {
    scope,
    m,
    fmt,
    account,
    accountId,
    contract,
    pos,
    netQty,
    bid,
    ask,
    ctx,
    placeGate,
    exitGate,
    flattenAllGate,
    priceText,
    place,
    market,
    join,
    exitSymbol,
    cancelAll,
    flatten,
    reverse,
    hotkey,
    preset,
    busyKey: (k: string) => `${scope}:${k}`,
  };
}

export type OrderEntry = ReturnType<typeof useOrderEntry>;

/** Busy flag of one of a surface's controls. */
export function useEntryBusy(e: OrderEntry, key: string): boolean {
  return useBusy(e.busyKey(key));
}
